import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Receipt, Wallet, Fingerprint, Loader2 } from 'lucide-react';
import { usarPasskey, errorPasskey, passkeysSoportadas } from '../lib/webauthn';

export default function Login({ onLogin }) {
  const isLogin = true; // el registro público está cerrado: sólo login
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [huella, setHuella] = useState(false);

  const finalizar = (data) => {
    const userData = {
      username: data.username,
      role: data.role || 'admin',
      full_name: data.full_name || data.username,
      permissions: data.permissions || [],
    };
    localStorage.setItem('user', JSON.stringify(userData));
    toast.success(data.passkey_usada ? `¡Bienvenido! (${data.passkey_usada})` : '¡Bienvenido!');
    onLogin(data.access_token, userData);
  };

  // Ingreso directo con la huella del dispositivo (sin usuario ni contraseña)
  const entrarConHuella = async () => {
    setHuella(true);
    try {
      const { data: o } = await axios.post(`${API}/passkeys/login/options`, {});
      const credential = await usarPasskey(o.options);
      const { data } = await axios.post(`${API}/passkeys/login/verify`,
        { token: o.token, credential });
      finalizar(data);
    } catch (error) {
      if (error?.response) toast.error(error.response.data?.detail || 'No se pudo verificar la huella');
      else toast.error(errorPasskey(error));
    } finally {
      setHuella(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const data = isLogin 
        ? { username, password }
        : { username, password, full_name: fullName };

      const response = await axios.post(`${API}${endpoint}`, data);

      finalizar(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en autenticación');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-8 items-center">
        {/* Left side - Branding */}
        <div className="text-white space-y-6 hidden md:block">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-white/20 backdrop-blur-sm p-4 rounded-2xl">
              <Wallet className="w-10 h-10" />
            </div>
            <h1 className="text-5xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>ReceiptVault</h1>
          </div>
          <h2 className="text-4xl font-bold leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Gestiona tus comprobantes con inteligencia artificial
          </h2>
          <p className="text-xl text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
            Arrastra, extrae y organiza todos tus comprobantes bancarios automáticamente con OCR avanzado.
          </p>
          <div className="flex gap-6 mt-8">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl">
              <Receipt className="w-8 h-8 mb-2" />
              <h3 className="font-semibold text-lg">OCR Inteligente</h3>
              <p className="text-sm text-white/80">Extracción automática de datos</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl">
              <Wallet className="w-8 h-8 mb-2" />
              <h3 className="font-semibold text-lg">Multi-Cuenta</h3>
              <p className="text-sm text-white/80">Organiza por grupos</p>
            </div>
          </div>
        </div>

        {/* Right side - Login form */}
        <Card className="w-full shadow-2xl border-0">
          <CardHeader className="space-y-2">
            <CardTitle className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </CardTitle>
            <CardDescription className="text-base">
              {isLogin ? 'Ingresa a tu cuenta para continuar' : 'Crea una cuenta nueva para comenzar'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLogin && passkeysSoportadas() && (
              <div className="mb-5">
                <Button type="button" variant="outline" disabled={huella || loading}
                  onClick={entrarConHuella} data-testid="login-huella-btn"
                  className="w-full h-11 text-base font-semibold border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800 flex items-center justify-center gap-2">
                  {huella ? <Loader2 className="w-5 h-5 animate-spin" /> : <Fingerprint className="w-5 h-5" />}
                  {huella ? 'Esperando la huella…' : 'Entrar con huella'}
                </Button>
                <div className="flex items-center gap-3 mt-5">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-xs text-gray-400">o con tu contraseña</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium">Nombre Completo</Label>
                  <Input
                    id="fullName"
                    data-testid="fullname-input"
                    placeholder="Tu nombre completo"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="h-11"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium">Usuario</Label>
                <Input
                  id="username"
                  data-testid="username-input"
                  placeholder="tu_usuario"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Contraseña</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                />
                {!isLogin && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-xs font-semibold text-blue-900 mb-1">Requisitos de seguridad:</p>
                    <ul className="text-xs text-blue-800 space-y-1">
                      <li className="flex items-center gap-1">
                        <span className={password.length >= 8 ? "text-green-600" : "text-gray-400"}>
                          {password.length >= 8 ? "✓" : "○"}
                        </span>
                        Mínimo 8 caracteres
                      </li>
                      <li className="flex items-center gap-1">
                        <span className={/[A-Z]/.test(password) ? "text-green-600" : "text-gray-400"}>
                          {/[A-Z]/.test(password) ? "✓" : "○"}
                        </span>
                        Al menos 1 mayúscula
                      </li>
                      <li className="flex items-center gap-1">
                        <span className={/[a-z]/.test(password) ? "text-green-600" : "text-gray-400"}>
                          {/[a-z]/.test(password) ? "✓" : "○"}
                        </span>
                        Al menos 1 minúscula
                      </li>
                      <li className="flex items-center gap-1">
                        <span className={/[0-9]/.test(password) ? "text-green-600" : "text-gray-400"}>
                          {/[0-9]/.test(password) ? "✓" : "○"}
                        </span>
                        Al menos 1 número
                      </li>
                      <li className="flex items-center gap-1">
                        <span className={/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) ? "text-green-600" : "text-gray-400"}>
                          {/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) ? "✓" : "○"}
                        </span>
                        Al menos 1 carácter especial
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              <Button 
                type="submit" 
                data-testid="auth-submit-btn"
                className="w-full h-11 text-base font-semibold"
                disabled={loading}
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
              </Button>
            </form>
            <p className="mt-6 text-center text-xs text-gray-400">
              Acceso privado. Las cuentas las crea el administrador desde el sistema.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Footer con links legales */}
      <div className="w-full max-w-6xl mt-8 text-center">
        <div className="flex justify-center items-center gap-4 text-sm text-white/80">
          <Link 
            to="/terminos-y-condiciones" 
            className="hover:text-white transition-colors underline"
            target="_blank"
          >
            Términos y Condiciones
          </Link>
          <span>•</span>
          <Link 
            to="/politica-de-privacidad" 
            className="hover:text-white transition-colors underline"
            target="_blank"
          >
            Política de Privacidad
          </Link>
        </div>
        <p className="text-xs text-white/60 mt-2">
          © 2025 Evotech Solutions S.R.L. - CUIT: 30-71234567-8
        </p>
      </div>
    </div>
  );
}